import type {CopilotSpaceResource, CopilotSpaceRepositoryBackedResource} from '../types'

interface ResourcesForDuplicationResult {
  resourcesToSave: CopilotSpaceResource[]
  resourcesThatWillNotTransfer: CopilotSpaceRepositoryBackedResource[]
}

/**
 * Determines which resources can be saved when duplicating a space to a new owner
 * and which will not transfer due to access restrictions.
 *
 * Private resources owned by a different organization/user than the target owner
 * will not be transferable if the target owner is an Organization.
 *
 * If the target owner is an individual user, all resources can be transferred.
 *
 * @param resources - The resources from the original space
 * @param targetOwnerId - The ID of the new owner for the duplicated space
 * @param targetOwnerIsOrganization - Whether the new owner is an organization
 * @returns An object containing resources that can be saved and those that cannot transfer
 */
export function resourcesForDuplication(
  resources: CopilotSpaceResource[],
  targetOwnerId: number,
  targetOwnerIsOrganization: boolean,
): ResourcesForDuplicationResult {
  const resourcesToSave: CopilotSpaceResource[] = []
  const resourcesThatWillNotTransfer: CopilotSpaceRepositoryBackedResource[] = []

  if (targetOwnerIsOrganization) {
    // If the target owner is an organization, we need to check for organization-level access
    for (const resource of resources) {
      // Check if this is a repository-backed resource with ownership and privacy info
      if ('ownerId' in resource && 'private' in resource && resource.private && resource.ownerId !== targetOwnerId) {
        // Private resource owned by different owner - cannot transfer
        resourcesThatWillNotTransfer.push(resource)
      } else {
        // Resource can be saved
        resourcesToSave.push(resource)
      }
    }

    return {
      resourcesToSave,
      resourcesThatWillNotTransfer,
    }
  } else {
    // If the target owner is a user then they can save all resources, even if the resource is org-owned and private.
    // This is because the resources in the space are already filtered to what the current user can see, so we can safely
    // assume duplicating the current user will preserve access to all resources.
    return {
      resourcesToSave: resources,
      resourcesThatWillNotTransfer: [],
    }
  }
}
